import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, Plus, Minus, Check, Clock, Package, Users } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import IndustriesSection from '../components/IndustriesSection';
import { useCMS } from '../context/CMSContext';
import { services as defaultServices, faqs as defaultFaqs } from '../data/mockData';

const PROCESS = [
  { id: 1, step: '01', title: 'Discover', body: 'Workshops, audits and stakeholder alignment. We come out with a real scope, not a stab in the dark.' },
  { id: 2, step: '02', title: 'Design', body: 'Wireframes, prototypes and a full design system — reviewed with you before a single line of production code is written.' },
  { id: 3, step: '03', title: 'Build', body: 'Component-driven engineering with performance, accessibility and Core Web Vitals baked in from day one.' },
  { id: 4, step: '04', title: 'Launch', body: 'Staged rollout, analytics, tracking and a post-launch monitoring window so nothing slips.' },
  { id: 5, step: '05', title: 'Grow', body: 'Monthly performance reviews, content, SEO and iterative optimization. Launch is the start, not the finish.' },
];

const ServiceDetailRow = ({ service, index, expanded, onToggle }) => {
  const isReverse = index % 2 === 1;
  const isOpen = expanded === service.id;
  return (
    <div id={service.slug || `service-${service.id}`} className="border-t border-white/5 py-14 lg:py-20 scroll-mt-24" data-testid={`service-detail-${service.id}`}>
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-start">
        <div className={`lg:col-span-5 ${isReverse ? 'lg:order-2' : ''}`}>
          <div className="flex items-center gap-4 mb-6">
            <span className="text-xs tracking-[0.25em] uppercase text-white/40 font-bold">{service.number}</span>
            <span className="w-8 h-px bg-white/20" />
          </div>
          <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white leading-tight">
            {service.title}
          </h3>
          <p className="mt-5 text-white/60 text-base lg:text-lg font-light leading-relaxed">
            {service.description}
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {(service.tags || []).map((t) => (
              <span key={t} className="px-3 py-1 rounded-full text-xs text-white/60 border border-white/10">{t}</span>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#050914] text-sm font-semibold hover:scale-[1.03] transition-transform">
              Book a Consultation <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
            </Link>
            {service.relatedStorySlug && (
              <Link to={`/success-stories/${service.relatedStorySlug}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white text-sm font-semibold hover:bg-white/5 transition-colors">
                Related success story
              </Link>
            )}
          </div>
        </div>
        <div className={`lg:col-span-7 ${isReverse ? 'lg:order-1' : ''}`}>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.015]">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3 flex items-center gap-2">
                <Users className="w-3.5 h-3.5" /> Who it&rsquo;s for
              </p>
              <p className="text-sm text-white/70 font-light leading-relaxed">{service.forWho}</p>
            </div>
            <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.015]">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" /> Timeline
              </p>
              <p className="text-sm text-white/70 font-light leading-relaxed">{service.timeline}</p>
            </div>
            <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.015] sm:col-span-2">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3 flex items-center gap-2">
                <Package className="w-3.5 h-3.5" /> Deliverables
              </p>
              <p className="text-sm text-white/70 font-light leading-relaxed">{service.deliverables}</p>
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/30 font-bold mt-4">Pricing · {service.priceNote}</p>
            </div>
          </div>

          <button onClick={() => onToggle(isOpen ? null : service.id)}
            className="mt-5 w-full flex items-center justify-between px-5 py-4 rounded-2xl border border-white/8 hover:border-white/20 text-left transition-colors"
            data-testid={`service-toggle-${service.id}`}>
            <span className="text-sm text-white font-semibold">{isOpen ? 'Hide details' : 'View includes, outcomes & tech'}</span>
            {isOpen ? <Minus className="w-4 h-4 text-white/60" /> : <Plus className="w-4 h-4 text-white/60" />}
          </button>
          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35 }} className="overflow-hidden">
                <div className="mt-5 grid sm:grid-cols-2 gap-5">
                  <div className="p-5 rounded-2xl border border-white/8">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3">Includes</p>
                    <ul className="space-y-2">
                      {(service.includes || []).map((it) => (
                        <li key={it} className="text-sm text-white/70 flex gap-2 items-start"><Check className="w-3.5 h-3.5 mt-1 text-[#FF6B35]" /><span>{it}</span></li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-5 rounded-2xl border border-white/8">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3">Business outcomes</p>
                    <ul className="space-y-2">
                      {(service.outcomes || []).map((it) => (
                        <li key={it} className="text-sm text-white/70 flex gap-2 items-start"><Check className="w-3.5 h-3.5 mt-1 text-[#FF6B35]" /><span>{it}</span></li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-5 rounded-2xl border border-white/8 sm:col-span-2">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3">Technologies</p>
                    <div className="flex flex-wrap gap-2">
                      {(service.tech || []).map((t) => (
                        <span key={t} className="px-3 py-1 rounded-full text-xs text-white/70 border border-white/15">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

const FAQRow = ({ faq, isOpen, onToggle }) => (
  <div className="border-t border-white/8">
    <button onClick={onToggle} className="w-full flex items-center justify-between py-6 text-left group" data-testid={`faq-toggle-${faq.id}`}>
      <span className="font-display text-lg md:text-xl text-white/90 group-hover:text-white transition-colors">{faq.q}</span>
      {isOpen ? <Minus className="w-4 h-4 text-white/50" /> : <Plus className="w-4 h-4 text-white/50" />}
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }} className="overflow-hidden">
          <p className="pb-6 text-white/60 text-sm lg:text-base font-light max-w-3xl leading-relaxed">{faq.a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const ServicesPage = () => {
  const { get } = useCMS();
  // Merge CMS metadata with local rich fields (forWho / timeline / includes / outcomes / tech)
  const cmsServices = get('services') || [];
  const services = defaultServices.map((local) => {
    const cms = cmsServices.find((c) => c.id === local.id);
    return cms ? { ...local, ...cms } : local;
  });
  const faqs = get('faqs') || defaultFaqs;
  const [expanded, setExpanded] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-[#050914]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-16 lg:pt-44 lg:pb-24 overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 hero-mesh" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <p className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/50 font-bold mb-6">
              <span className="w-8 h-px bg-white/30" /> Services
            </p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter text-white leading-[1.02]">
              Understand how it all comes together.
            </h1>
            <p className="mt-7 text-white/60 text-lg max-w-2xl font-light leading-relaxed">
              We combine strategy, design, engineering, and post-launch growth into a structured path for your success.
            </p>
          </div>
          <div className="lg:col-span-4 lg:pt-16">
            <div className="grid grid-cols-3 gap-3">
              {services.map((s) => (
                <a key={s.id} href={`#service-${s.id}`}
                  className="group aspect-square w-full flex items-center justify-center rounded-full border border-white/10 hover:border-white/35 bg-white/[0.02] hover:bg-white/[0.05] transition-all duration-300 p-2 text-center"
                >
                  <span className="text-[10px] md:text-xs leading-tight text-white/70 group-hover:text-white transition-colors">
                    {s.title}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service rows */}
      <section className="relative pb-8 lg:pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {services.map((s, i) => (
            <ServiceDetailRow key={s.id} service={s} index={i} expanded={expanded} onToggle={setExpanded} />
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="relative py-24 lg:py-32 border-t border-white/5" data-testid="services-process">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/50 font-bold mb-6">
            <span className="w-8 h-px bg-white/30" /> Our Process
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tighter text-white leading-[1.05] max-w-3xl mb-16">
            A repeatable, senior-led delivery model.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {PROCESS.map((p, i) => (
              <motion.div key={p.id}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                className="p-6 rounded-2xl border border-white/8 min-h-[200px] flex flex-col">
                <span className="text-xs tracking-[0.25em] text-white/30 font-bold">{p.step}</span>
                <h4 className="mt-3 font-display text-xl text-white">{p.title}</h4>
                <p className="mt-3 text-sm text-white/55 font-light leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <IndustriesSection />

      {/* FAQ */}
      <section className="relative py-24 lg:py-32 border-t border-white/5" data-testid="services-faq">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <p className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/50 font-bold mb-6">
            <span className="w-8 h-px bg-white/30" /> FAQ
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tighter text-white leading-[1.05] mb-12">
            Frequently asked.
          </h2>
          <div>
            {faqs.map((f) => (
              <FAQRow key={f.id} faq={f} isOpen={openFaq === f.id} onToggle={() => setOpenFaq(openFaq === f.id ? null : f.id)} />
            ))}
            <div className="border-t border-white/8" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
